import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { settlementsAPI } from '../services/api';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from './ui/alert-dialog';
import { Banknote, Loader2, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

const fmt = (n) => `${(n || 0).toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} zł`;

const DepositTodayButton = () => {
  const navigate = useNavigate();
  const [preview, setPreview] = useState({ amount_total: 0, count: 0, already_settled: false });
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const r = await settlementsAPI.depositTodayPreview();
      setPreview(r.data);
    } catch { /* silent */ } finally { setLoading(false); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const submit = async () => {
    setSubmitting(true);
    try {
      const r = await settlementsAPI.depositToday('Quick deposit z dashboardu');
      toast.success(`Rozliczono ${fmt(r.data.amount_total)} (${r.data.payments_count} wpłat)`);
      setOpen(false);
      refresh();
    } catch (e) {
      const detail = e.response?.data?.detail || 'Błąd';
      if (e.response?.status === 409) toast.warning(detail);
      else toast.error(detail);
    } finally { setSubmitting(false); }
  };

  // Hide if nothing to deposit and not yet settled — keeps dashboard clean
  if (loading) {
    return (
      <Card className="border-l-4 border-l-amber-500">
        <CardContent className="py-3 flex items-center gap-3">
          <Loader2 className="w-4 h-4 animate-spin text-amber-500" />
          <span className="text-sm text-muted-foreground">Sprawdzam dzisiejszą gotówkę...</span>
        </CardContent>
      </Card>
    );
  }
  if (preview.already_settled) {
    return (
      <Card className="border-l-4 border-l-emerald-500 bg-emerald-50/30">
        <CardContent className="py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <CheckCircle2 className="w-5 h-5 text-emerald-500" />
            <div>
              <p className="text-sm font-semibold text-navy-800">Dzisiejsza gotówka rozliczona</p>
              <p className="text-xs text-muted-foreground">Możesz utworzyć kolejne rozliczenie ręcznie w „Rozliczenia"</p>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={() => navigate('/rozliczenia-gotowki')}>Szczegóły</Button>
        </CardContent>
      </Card>
    );
  }
  if (preview.count === 0) return null;

  return (
    <>
      <Card className="border-l-4 border-l-amber-500 bg-gradient-to-r from-amber-50 to-white">
        <CardContent className="py-4 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
              <Banknote className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="font-semibold text-navy-800">Wpłać dzisiejszą gotówkę</p>
              <p className="text-xs text-muted-foreground">
                Masz <strong className="text-amber-700">{fmt(preview.amount_total)}</strong> w {preview.count} wpłat{preview.count === 1 ? 'cie' : preview.count < 5 ? 'ach' : 'ach'} do rozliczenia
              </p>
            </div>
          </div>
          <Button
            onClick={() => setOpen(true)}
            className="bg-amber-500 hover:bg-amber-600 text-white shadow"
            data-testid="deposit-today-btn"
          >
            <Banknote className="w-4 h-4 mr-2" />Rozlicz teraz
          </Button>
        </CardContent>
      </Card>

      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent data-testid="deposit-today-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Banknote className="w-5 h-5 text-amber-500" />
              Rozliczyć dzisiejszą gotówkę?
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <div className="flex justify-between text-sm"><span>Liczba wpłat:</span><strong>{preview.count}</strong></div>
                  <div className="flex justify-between text-lg mt-1"><span>Łącznie:</span><strong className="text-amber-700">{fmt(preview.amount_total)}</strong></div>
                </div>
                <p className="text-xs text-muted-foreground">
                  Wszystkie dzisiejsze nierozliczone wpłaty gotówkowe zostaną oznaczone jako „rozliczone" i przypisane do nowego rekordu rozliczeniowego z datą dzisiejszą.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Anuluj</AlertDialogCancel>
            <AlertDialogAction onClick={submit} disabled={submitting} className="bg-amber-500 hover:bg-amber-600 text-white" data-testid="deposit-today-confirm">
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Banknote className="w-4 h-4 mr-2" />}
              Potwierdź wpłatę
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default DepositTodayButton;
